import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, MessageSquare, AlertCircle, Lightbulb, ThumbsUp, Wrench, HelpCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const CATEGORIES = [
  { value: "complaint",     label: "Complaint",          desc: "Something went wrong. Tell us.",                 Icon: AlertCircle },
  { value: "product_issue", label: "Product Issue",      desc: "An issue with a flavour or batch.",              Icon: Wrench },
  { value: "service",       label: "Service Feedback",   desc: "Delivery, retailer experience, support.",        Icon: MessageSquare },
  { value: "suggestion",    label: "Suggestion",         desc: "Something you'd love to see us try.",            Icon: Lightbulb },
  { value: "improvement",   label: "Improvement Idea",   desc: "Something we could do better.",                  Icon: ThumbsUp },
  { value: "feedback",      label: "General Feedback",   desc: "Anything else — kind words count too.",          Icon: HelpCircle },
];

export default function Complaints() {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", category: "feedback", subject: "", description: "", image_url: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/complaints", form);
      setSubmitted(true);
      toast.success("Thank you — we'll review and reach out if needed.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div data-testid="complaints-done" className="section-pad">
        <div className="container-pp max-w-2xl mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Received</div>
          <h1 className="editorial-h1 text-5xl md:text-6xl mt-3">Thank you, {form.name.split(" ")[0]}.</h1>
          <p className="mt-5 text-brand-charcoal/70 leading-relaxed">We read every entry. If you've shared contact details and it needs a reply, someone from our team will reach out personally — usually within 48 hours.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/" className="btn-pill btn-dark text-sm" data-testid="cm-back-home">Back to home</Link>
            <Link to="/feedback" className="btn-pill btn-outline-dark text-sm" data-testid="cm-leave-review">Leave a public review</Link>
          </div>
        </div>
      </div>
    );
  }

  const selected = CATEGORIES.find((c) => c.value === form.category);

  return (
    <div data-testid="complaints-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><Sparkles size={12} /> Complaints &amp; Feedback</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              Tell us what we got <em className="font-display italic">right</em> — and what we got <em className="font-display italic">wrong</em>.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/75 leading-relaxed">
            <p>This is a private channel for customers, retailers, partners and visitors to share complaints, product issues, service feedback, suggestions and improvement ideas.</p>
            <p className="mt-3 text-brand-charcoal">We read every single one — and we get back to you if you'd like us to.</p>
          </div>
        </div>

        {/* Category cards */}
        <div className="mt-16">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Pick a category</div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cm-categories">
            {CATEGORIES.map((c) => {
              const active = form.category === c.value;
              return (
                <button type="button" key={c.value} data-testid={`cm-cat-${c.value}`} onClick={() => setForm({ ...form, category: c.value })}
                  className={`text-left card-elevated p-5 transition-all ${active ? "ring-2 ring-brand-charcoal -translate-y-0.5" : "hover:-translate-y-0.5"}`}>
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${active ? "bg-brand-charcoal text-brand-yellow" : "bg-brand-yellow/25 text-brand-charcoal"}`}>
                    <c.Icon size={18} />
                  </div>
                  <div className="font-display text-xl mt-3">{c.label}</div>
                  <div className="text-xs text-brand-charcoal/65 mt-1">{c.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} data-testid="cm-form" className="mt-16 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">You picked</div>
            <div className="font-display text-4xl mt-2">{selected?.label}</div>
            <p className="mt-4 text-brand-charcoal/70 leading-relaxed">{selected?.desc}</p>
            <div className="mt-8 card-elevated p-6 bg-brand-yellow/15 border border-brand-yellow/40">
              <div className="font-display text-xl">Your privacy</div>
              <p className="mt-2 text-sm text-brand-charcoal/75">Phone &amp; email are optional. We won't share what you write with anyone outside the Pop Panda team.</p>
            </div>
          </div>

          <div className="lg:col-span-7 card-elevated p-8 lg:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Name</label><input data-testid="cm-name" required className="pp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Contact Number (optional)</label><input data-testid="cm-phone" className="pp-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email (optional)</label><input type="email" data-testid="cm-email" className="pp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Subject</label><input data-testid="cm-subject" required className="pp-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="One line about what this is" /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Description</label><textarea data-testid="cm-desc" required rows="6" className="pp-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell us everything we need to know." /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Image URL (optional)</label><input data-testid="cm-image" className="pp-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Paste an image link if it helps explain" /></div>
            </div>
            <button data-testid="cm-submit" disabled={busy} className="btn-pill btn-dark inline-flex items-center gap-2">{busy ? "Sending…" : "Submit"} <ArrowUpRight size={14} /></button>
          </div>
        </form>
      </div>
    </div>
  );
}
